import { useLogger } from '@react-navigation/devtools'
import type { NavigationContainerRef } from '@react-navigation/native'
import type * as SentryModule from '@sentry/react'
import { useNavigationContainerRef } from 'expo-router'
import type React from 'react'
import { useEffect, type PropsWithChildren, type ReactNode } from 'react'

import { useMeQuery } from '@/src/features/auth/services/useMeQuery'

import { env } from '../env'

let sentryScope: typeof SentryModule | null = null

function getSentry() {
  if (!sentryScope) {
    sentryScope = require('@sentry/react')
  }
  return sentryScope
}

export const TouchEventBoundary = ({ children }: PropsWithChildren): ReactNode => children
export const wrap = <T extends object>(element: T) => element

/**
 * Encrypts username with HMAC-SHA256
 * @param username
 * @returns
 */
async function encryptUsername(username: string) {
  if (!env.SENTRY_HMAC_IV || !env.SENTRY_HMAC_KEY) {
    if (__DEV__) {
      console.warn('SENTRY_HMAC_IV or SENTRY_HMAC_KEY not set')
    }
    return { encryptedData: '' }
  }
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoder = new TextEncoder()
    const hmacKeyBase64 = env.SENTRY_HMAC_KEY
    const keyBuffer = Uint8Array.from(atob(hmacKeyBase64), (c) => c.charCodeAt(0))
    const ivBuffer = Uint8Array.from(atob(env.SENTRY_HMAC_IV), (c) => c.charCodeAt(0))

    const keyMaterial = await window.crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-GCM' }, false, ['encrypt'])

    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: ivBuffer },
      keyMaterial,
      encoder.encode(username),
    )

    return {
      encryptedData: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
      iv: btoa(String.fromCharCode(...ivBuffer)), // Store IV with encryption
    }
  } else {
    return { encryptedData: username } // Fallback for non-web environments
  }
}

export const SentryNavTracing = () => {
  const ref = useNavigationContainerRef()
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- ref bridge
  useLogger(ref as React.RefObject<NavigationContainerRef<ReactNavigation.RootParamList>>)

  const { data: me } = useMeQuery()

  useEffect(() => {
    if (me?.id && env.SENTRY_HMAC_KEY && window.crypto?.subtle != null) {
      // We encrypt username to avoid sending raw data to Sentry
      encryptUsername(me.name).then((value) => {
        getSentry()?.getCurrentScope().setUser({ username: value.encryptedData })
      })
    }
  }, [me?.name, me?.id])

  return null
}
